"use client"

import { useLocale, useTranslations } from "next-intl"
import { useEffect, useMemo } from "react"
import { Controller, FieldPath, FieldValues, useFormContext } from "react-hook-form"
import { useIsMobile } from "../../hooks/use-mobile"
import { TSelect } from "../../ui/select/TSelect"
import { FormControl, FormItem, FormMessage } from "../../ui/shadcn/form"
import { DynamicFormLabel } from "../components/form-label"
import type { ParsedField } from "../types"
import { toTitleCase } from "../utils/format-label"
import { tWithFallback } from "../utils/i18n"

const NONE_VALUE = "__NONE__"

interface EnumFieldProps {
	field: ParsedField
	titleCase?: boolean
	/** 메뉴 방향 — 소비자(앱)가 브레이크포인트에 맞게 결정. 미지정 시 모바일=bottom, 데스크톱=right */
	menuSide?: "right" | "bottom"
}

export function EnumField({ field, titleCase = false, menuSide }: EnumFieldProps) {
	const isMobile = useIsMobile()
	const resolvedMenuSide = menuSide ?? (isMobile ? "bottom" : "right")
	const t = useTranslations()
	const locale = useLocale()
	const { name, schema, required } = field
	const description = schema.description
	const title = schema.title
	const { control, watch, setValue } = useFormContext()

	// required=false이고 default가 없을 때만 NONE_VALUE 추가
	const enumValues = useMemo(() => {
		if (!schema.enum) return []
		const values = schema.enum as Array<string | number>
		return !required && schema.default === undefined ? [NONE_VALUE, ...values] : values
	}, [schema.enum, required, schema.default])
	const defaultValue = schema.default ?? enumValues[0]

	// oneOf 조건 변경으로 enum이 바뀌었을 때, 현재 값이 유효하지 않으면 자동 리셋
	const currentValue = watch(name)
	useEffect(() => {
		if (!schema.enum) return
		const validDefault = schema.default ?? enumValues[0]
		if (currentValue !== undefined && currentValue !== NONE_VALUE && !enumValues.includes(currentValue)) {
			setValue(name as FieldPath<FieldValues>, validDefault as never, { shouldValidate: false })
		}
	}, [schema.enum, schema.default, enumValues, currentValue, setValue, name])

	if (!schema.enum) return null

	// x-enum-labels 지원: { "value": "Display Label" } 형식
	const enumLabels = schema["x-enum-labels"]
	const getLabel = (value: string | number) => {
		if (enumLabels?.[String(value)]) return enumLabels[String(value)]
		// optional enum(required=false·default 없음)의 "미선택" 옵션 라벨. 코어 컴포넌트라
		// 다른 앱(editgen 등) 소비자도 있어, 키 없는 로케일/앱에서는 fallback "None" 을 그대로 유지한다.
		if (value === NONE_VALUE) return tWithFallback(t, locale, "creagen.dynamic-form.enumField.none", "None")
		return titleCase ? toTitleCase(value) : String(value)
	}

	const items = enumValues.map((value) => ({ value, label: getLabel(value) }))

	return (
		<Controller
			name={name as FieldPath<FieldValues>}
			control={control}
			render={({ field }) => (
				<FormItem>
					<FormControl>
						<TSelect
							side={resolvedMenuSide}
							name={name}
							value={field.value ?? defaultValue}
							items={items}
							onChange={(value) => field.onChange(value === NONE_VALUE ? undefined : value)}
							renderValue={(value) => (
								<div className="flex flex-col items-start gap-0.5">
									<DynamicFormLabel name={name} title={title} required={required} description={description} />
									<span>{getLabel(value)}</span>
								</div>
							)}
							className="[&_button]:h-14! [&_button]:py-2! [&_button]:px-3! [&_button_.icon]:text-g600! group"
							contentClassName="max-h-[min(400px,var(--radix-select-content-available-height))]"
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
